// utils/kioskConfig.js
/**
 * Kiosk Configuration Manager
 * Handles screen ID detection from URL or localStorage
 */

const STORAGE_KEY = 'kiosk_screen_id';
const API_BASE_KEY = 'kiosk_api_base';

/**
 * Get the kiosk screen ID
 * Priority: URL param > localStorage > null
 */
export function getKioskScreenId() {
  // Priority 1: URL parameter (for testing/override)
  const urlParams = new URLSearchParams(window.location.search);
  const urlScreenId = urlParams.get('screen_id');
  
  if (urlScreenId && urlScreenId.trim()) {
    // Save to localStorage for persistence
    localStorage.setItem(STORAGE_KEY, urlScreenId);
    return urlScreenId;
  }
  
  // Priority 2: LocalStorage (permanent config)
  const storedScreenId = localStorage.getItem(STORAGE_KEY);
  if (storedScreenId) {
    return storedScreenId;
  }
  
  // Priority 3: Not configured
  return null;
}

/**
 * Set the kiosk screen ID
 */
export function setKioskScreenId(screenId) {
  if (!screenId) {
    localStorage.removeItem(STORAGE_KEY);
  } else {
    localStorage.setItem(STORAGE_KEY, screenId);
  }
}

/**
 * Clear kiosk configuration (for setup reset)
 */
export function clearKioskConfig() {
  localStorage.removeItem(STORAGE_KEY);
  // Don't clear API base as it's environment-specific
}

/**
 * Get API base URL
 */
export function getKioskApiBase() {
  // Priority 1: URL parameter
  const urlParams = new URLSearchParams(window.location.search);
  const urlApiBase = urlParams.get('api_base');
  
  if (urlApiBase) {
    localStorage.setItem(API_BASE_KEY, urlApiBase);
    return urlApiBase;
  }
  
  // Priority 2: LocalStorage
  const storedApiBase = localStorage.getItem(API_BASE_KEY);
  if (storedApiBase) {
    return storedApiBase;
  }
  
  // Priority 3: Relative URLs (Vite proxy handles routing)
  return import.meta.env.VITE_RCC_BASE || '';
}

/**
 * Check if kiosk is configured
 */
export function isKioskConfigured() {
  return getKioskScreenId() !== null;
}

/**
 * Get full kiosk configuration
 */
export function getKioskConfig() {
  return {
    screenId: getKioskScreenId(),
    apiBase: getKioskApiBase(),
    isConfigured: isKioskConfigured()
  };
}
